import React, { useState, useCallback, useEffect, useMemo } from "react";
import {
    Button,
    TextField,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Stack,
    Box,
    Typography,
    CircularProgress,
} from "@mui/material";
import { useTranslation } from 'react-i18next';
import materialIconNames from "src/data/materialIconNames";
import { get, post } from "src/services/content.service";
import { useDispatch } from "react-redux";
import { showAlert } from "src/redux/slices/AlertSlice";
import axios from "axios";


const AddNodeDialog = ({
    title,
    contentText,
    textLabel,
    onSubmit,
    open,
    onClose,
    validationList,
    errorText,
    parentNodeTitle,
    initialIcon,
    initialIconFile,
    initialTitle,
    initialNodeType,
    initialPosition,
    hidePlacement,
    disableTitle,
    typeOptions,
}) => {
    const [nodeName, setNodeName] = useState("");
    const [errorState, setErrorState] = useState(1);
    const [position, setPosition] = useState(0);
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const NAV_TYPES = [
        { value: "content_list", label: t('AddNodeDialog.NavTypes.content_list') },
        { value: "tab", label: t('AddNodeDialog.NavTypes.tab') },
        { value: "nested_list", label: t('AddNodeDialog.NavTypes.nested_list') },
        { value: "list", label: t('AddNodeDialog.NavTypes.list') },
        { value: "content", label: t('AddNodeDialog.NavTypes.content') },
    ];
    const resolvedTypeOptions =
        Array.isArray(typeOptions) && typeOptions.length > 0
            ? typeOptions
            : NAV_TYPES;

    const [nodeType, setNodeType] = useState(NAV_TYPES[0].value);
    const [iconMode, setIconMode] = useState("none");
    const [iconSearch, setIconSearch] = useState("");
    const [customSearch, setCustomSearch] = useState("");
    const [customIcons, setCustomIcons] = useState([]);
    const [customIconsLoading, setCustomIconsLoading] = useState(false);
    const [selectedIcon, setSelectedIcon] = useState("");
    const [selectedIconFile, setSelectedIconFile] = useState(null);
    const [uploadingIcon, setUploadingIcon] = useState(false);

    const handleChange = (event) => {
        const newValue = event.target.value;
        if (newValue === "") {
            setErrorState(1);
        } else if (
            validationList.some(
                (x) => x.toLowerCase() === newValue.toLowerCase()
            )
        ) {
            setErrorState(2);
        } else {
            setErrorState(0);
        }
        setNodeName(newValue);
    };

    const getHelperText = () => {
        switch (errorState) {
            case 1:
                return errorText.empty;
            case 2:
                return errorText.alreadyExists;
            default:
                return "";
        }
    };

    const handleSubmit = () => {
        if (iconMode === "custom" && !selectedIconFile) {
            dispatch(
                showAlert({
                    message: t("NavSection.ContextMenu.iconMissing"),
                    severity: "error",
                })
            );
            return;
        }
        const iconValue = iconMode === "predefined" ? selectedIcon : "";
        const iconFileId =
            iconMode === "custom" && selectedIconFile
                ? selectedIconFile.id
                : null;
        onSubmit({
            nodeName,
            nodeType,
            position,
            icon: iconValue,
            iconFileId,
            iconFile: selectedIconFile,
        });
        setNodeName("");
        setPosition(0);
        setNodeType(NAV_TYPES[0].value);
        setIconMode("none");
        setIconSearch("");
        setCustomSearch("");
        setSelectedIcon("");
        setSelectedIconFile(null);
        onClose();
    };

    const resolveFileUrl = (path) => {
        if (!path) return "";
        if (path.startsWith("http://") || path.startsWith("https://")) {
            return path;
        }
        const baseUrl = axios.defaults.baseURL || "";
        if (!baseUrl) return path;
        if (path.startsWith("/")) {
            return `${baseUrl}${path}`;
        }
        return `${baseUrl}/${path}`;
    };

    const loadCustomIcons = useCallback(async () => {
        setCustomIconsLoading(true);
        try {
            const res = await get(dispatch, `/api/content/nav/icons`);
            setCustomIcons(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            dispatch(
                showAlert({
                    message: t("NavSection.ContextMenu.iconLoadError"),
                    severity: "error",
                })
            );
        } finally {
            setCustomIconsLoading(false);
        }
    }, [dispatch, t]);

    const handleIconUpload = async (file) => {
        if (!file) return;
        if (!["image/png", "image/svg+xml"].includes(file.type)) {
            dispatch(
                showAlert({
                    message: t("NavSection.ContextMenu.iconTypeError"),
                    severity: "error",
                })
            );
            return;
        }
        setUploadingIcon(true);
        try {
            const reader = new FileReader();
            const dataUrl = await new Promise((resolve, reject) => {
                reader.onload = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(file);
            });
            const payload = {
                file: {
                    name: file.name,
                    size: file.size,
                    type: file.type,
                    file: dataUrl,
                },
            };
            const res = await post(dispatch, `/api/content/nav/icons`, payload);
            const newIcon = res.data;
            setCustomIcons((prev) => [newIcon, ...prev]);
            setIconMode("custom");
            setSelectedIconFile(newIcon);
            setSelectedIcon("");
        } catch (err) {
            dispatch(
                showAlert({
                    message: t("NavSection.ContextMenu.iconUploadError"),
                    severity: "error",
                })
            );
        } finally {
            setUploadingIcon(false);
        }
    };

    const filteredPredefinedIcons = useMemo(() => {
        const term = iconSearch.trim().toLowerCase();
        if (!term) return materialIconNames;
        return materialIconNames.filter((name) =>
            name.toLowerCase().includes(term)
        );
    }, [iconSearch]);

    const filteredCustomIcons = useMemo(() => {
        const term = customSearch.trim().toLowerCase();
        if (!term) return customIcons;
        return customIcons.filter((icon) =>
            (icon.fileName || "").toLowerCase().includes(term)
        );
    }, [customIcons, customSearch]);

    useEffect(() => {
        if (!open) return;
        loadCustomIcons();
        if (initialIconFile) {
            setIconMode("custom");
            setSelectedIconFile(initialIconFile);
            setSelectedIcon("");
        } else if (initialIcon) {
            setIconMode("predefined");
            setSelectedIcon(initialIcon);
            setSelectedIconFile(null);
        } else {
            setIconMode("none");
            setSelectedIcon("");
            setSelectedIconFile(null);
        }
        if (initialTitle) {
            setNodeName(initialTitle);
            setErrorState(0);
        } else {
            setNodeName("");
            setErrorState(1);
        }
        if (typeof initialNodeType === "string") {
            setNodeType(initialNodeType);
        } else {
            setNodeType(resolvedTypeOptions[0].value);
        }
        if (typeof initialPosition === "number") {
            setPosition(initialPosition);
        } else {
            setPosition(0);
        }
    }, [
        open,
        loadCustomIcons,
        initialIcon,
        initialIconFile,
        initialTitle,
        initialNodeType,
        initialPosition,
    ]);

    return (
        <Dialog open={open} onClose={onClose}>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                <DialogContentText>{contentText}</DialogContentText>
                <Stack spacing={2}>
                    <TextField
                        error={errorState === 2 ? true : false}
                        autoFocus
                        margin="dense"
                        id="name"
                        value={nodeName}
                        onChange={handleChange}
                        label={textLabel}
                        type="text"
                        fullWidth
                        variant="standard"
                        disabled={disableTitle}
                        helperText={getHelperText()}
                    />
                    {!hidePlacement && (
                        <Stack spacing={2} direction="row">
                            <FormControl fullWidth>
                                <InputLabel>{t('AddNodeDialog.insertLocation')}</InputLabel>
                                <Select
                                    value={position}
                                    label={t('AddNodeDialog.positionDropdown.label')}
                                    onChange={(event) =>
                                        setPosition(event.target.value)
                                    }
                                >
                                    <MenuItem
                                        value={0}
                                    >{t('AddNodeDialog.positionDropdown.under', { title: parentNodeTitle })}</MenuItem>
                                    <MenuItem
                                        value={1}
                                    >{t('AddNodeDialog.positionDropdown.inside', { title: parentNodeTitle })}</MenuItem>
                                    {/* <MenuItem
                                        value={1}
                                    >{`above ${parentNodeTitle}`}</MenuItem> */}
                                </Select>
                            </FormControl>
                            <FormControl fullWidth>
                                <InputLabel>{t('AddNodeDialog.nodeType')}</InputLabel>
                                <Select
                                    value={nodeType}
                                    label={t('AddNodeDialog.nodeType')}
                                    onChange={(event) =>
                                        setNodeType(event.target.value)
                                    }
                                >
                                {resolvedTypeOptions.map((item) => (
                                    <MenuItem key={item.value} value={item.value}>
                                        {item.label}
                                    </MenuItem>
                                ))}
                                </Select>
                            </FormControl>
                        </Stack>
                    )}
                    <Box sx={{ mt: 1 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            {t("NavSection.ContextMenu.iconLabel")}
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                            <Button
                                variant={
                                    iconMode === "none"
                                        ? "contained"
                                        : "outlined"
                                }
                                onClick={() => {
                                    setIconMode("none");
                                    setSelectedIcon("");
                                    setSelectedIconFile(null);
                                }}
                                size="small"
                            >
                                {t("NavSection.ContextMenu.iconNone")}
                            </Button>
                            <Button
                                variant={
                                    iconMode === "predefined"
                                        ? "contained"
                                        : "outlined"
                                }
                                onClick={() => {
                                    setIconMode("predefined");
                                    setSelectedIconFile(null);
                                }}
                                size="small"
                            >
                                {t("NavSection.ContextMenu.iconPredefined")}
                            </Button>
                            <Button
                                variant={
                                    iconMode === "custom"
                                        ? "contained"
                                        : "outlined"
                                }
                                onClick={() => setIconMode("custom")}
                                size="small"
                            >
                                {t("NavSection.ContextMenu.iconCustom")}
                            </Button>
                        </Stack>
                        {iconMode === "predefined" ? (
                            <>
                                <TextField
                                    fullWidth
                                    margin="dense"
                                    label={t("NavSection.ContextMenu.iconSearch")}
                                    value={iconSearch}
                                    onChange={(event) =>
                                        setIconSearch(event.target.value)
                                    }
                                />
                                <Box
                                    sx={{
                                        mt: 1,
                                        display: "grid",
                                        gridTemplateColumns:
                                            "repeat(auto-fill, minmax(44px, 1fr))",
                                        gap: 1,
                                        maxHeight: 200,
                                        overflowY: "auto",
                                        border: "1px solid",
                                        borderColor: "divider",
                                        borderRadius: 1,
                                        p: 1,
                                    }}
                                >
                                    {filteredPredefinedIcons.map((iconName) => (
                                        <Box
                                            key={iconName}
                                            onClick={() => {
                                                setSelectedIcon(iconName);
                                                setSelectedIconFile(null);
                                            }}
                                            sx={{
                                                width: 40,
                                                height: 40,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                borderRadius: 1,
                                                cursor: "pointer",
                                                border: "1px solid",
                                                borderColor:
                                                    selectedIcon === iconName
                                                        ? "primary.main"
                                                        : "transparent",
                                                bgcolor:
                                                    selectedIcon === iconName
                                                        ? "action.selected"
                                                        : "transparent",
                                            }}
                                        >
                                            <span className="material-icons">
                                                {iconName}
                                            </span>
                                        </Box>
                                    ))}
                                </Box>
                            </>
                        ) : iconMode === "custom" ? (
                            <>
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                    sx={{ mb: 1 }}
                                >
                                    <TextField
                                        fullWidth
                                        margin="dense"
                                        label={t("NavSection.ContextMenu.iconSearch")}
                                        value={customSearch}
                                        onChange={(event) =>
                                            setCustomSearch(event.target.value)
                                        }
                                    />
                                    <Button
                                        component="label"
                                        variant="outlined"
                                        size="small"
                                        disabled={uploadingIcon}
                                    >
                                        {t("NavSection.ContextMenu.iconUpload")}
                                        <input
                                            hidden
                                            type="file"
                                            accept="image/png,image/svg+xml"
                                            onChange={(event) => {
                                                const file =
                                                    event.target.files &&
                                                    event.target.files[0];
                                                if (file) {
                                                    handleIconUpload(file);
                                                }
                                                event.target.value = "";
                                            }}
                                        />
                                    </Button>
                                    {uploadingIcon && (
                                        <CircularProgress size={20} />
                                    )}
                                </Stack>
                                <Box
                                    sx={{
                                        display: "grid",
                                        gridTemplateColumns:
                                            "repeat(auto-fill, minmax(56px, 1fr))",
                                        gap: 1,
                                        maxHeight: 200,
                                        overflowY: "auto",
                                        border: "1px solid",
                                        borderColor: "divider",
                                        borderRadius: 1,
                                        p: 1,
                                    }}
                                >
                                    {customIconsLoading ? (
                                        <Box
                                            sx={{
                                                gridColumn: "1 / -1",
                                                display: "flex",
                                                justifyContent: "center",
                                                py: 2,
                                            }}
                                        >
                                            <CircularProgress size={24} />
                                        </Box>
                                    ) : (
                                        filteredCustomIcons.map((iconFile) => {
                                            const filePathRaw =
                                                iconFile.filePath ||
                                                iconFile.file_path;
                                            const filePath = resolveFileUrl(
                                                filePathRaw
                                            );
                                            return (
                                                <Box
                                                    key={iconFile.id}
                                                    onClick={() => {
                                                        setSelectedIconFile(
                                                            iconFile
                                                        );
                                                        setSelectedIcon("");
                                                    }}
                                                    sx={{
                                                        width: 52,
                                                        height: 52,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        borderRadius: 1,
                                                        cursor: "pointer",
                                                        border: "1px solid",
                                                        borderColor:
                                                            selectedIconFile &&
                                                            selectedIconFile.id ===
                                                                iconFile.id
                                                                ? "primary.main"
                                                                : "transparent",
                                                        bgcolor:
                                                            selectedIconFile &&
                                                            selectedIconFile.id ===
                                                                iconFile.id
                                                                ? "action.selected"
                                                                : "transparent",
                                                    }}
                                                >
                                                    {filePath ? (
                                                        <Box
                                                            component="img"
                                                            src={filePath}
                                                            alt={iconFile.fileName}
                                                            sx={{
                                                                width: 32,
                                                                height: 32,
                                                                objectFit:
                                                                    "contain",
                                                            }}
                                                        />
                                                    ) : null}
                                                </Box>
                                            );
                                        })
                                    )}
                                </Box>
                            </>
                        ) : null}
                    </Box>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t('buttonCommons.cancel')}</Button>
                <Button
                    disabled={errorState > 0 ? true : false}
                    onClick={() => {
                        handleSubmit();
                    }}
                >
                    {t('buttonCommons.submit')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
export default AddNodeDialog;
